import { useMutation, useQueryClient } from '@tanstack/react-query'

import { putRequest } from 'src/lib/api-client'
import { queries } from 'src/lib/query-keys'

import type { BusinessNote } from '../schemas/note-schema'

interface UpdateNoteParams {
  id: number
  content: string
  title: string
  referralShareId?: number
  leadInquiryId?: number
}

const updateNote = async (id: number, content: string, title: string): Promise<BusinessNote | undefined> => {
  return putRequest<BusinessNote>(`/business/notes/${id}`, { content, title })
}

/**
 * Hook to update an existing note
 */
export const useEditNote = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, content, title }: UpdateNoteParams) => updateNote(id, content, title),
    onSuccess: (updatedNote: BusinessNote | undefined, { referralShareId, leadInquiryId }) => {
      // Invalidate the notes list query to trigger a refetch
      if (referralShareId) {
        queryClient.invalidateQueries({
          queryKey: queries.notes.list({ referralShareId }).queryKey,
        })
      }
      if (leadInquiryId) {
        queryClient.invalidateQueries({
          queryKey: queries.notes.list({ leadInquiryId }).queryKey,
        })
      }

      // If we have a specific note ID, invalidate that specific note query too
      if (updatedNote?.id) {
        queryClient.invalidateQueries({
          queryKey: queries.notes.details(updatedNote.id).queryKey,
        })
      }
    },
  })
}
